// src/Pages/management/ProjectManagement/Projects.js
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Badge,
  Form,
  Spinner,
  Card,
  InputGroup,
  Modal,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  Search,
  Funnel,
  Eye,
  Trash,
  Calendar,
  Building,
  Person,
  CheckCircle,
  XCircle,
  ArrowClockwise,
  Pencil,
  GeoAlt,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [selectedProject, setSelectedProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  const primaryColor = "#302302ff";
  const secondaryColor = "#0d2b27ff";

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem("adminToken") || localStorage.getItem("token");

      const response = await axios.get(
        "http://34.206.193.218:4001/api/admin/adminProjects/allProjects",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            page: currentPage,
            limit: limit,
            search: searchTerm || undefined,
            status: filterStatus || undefined,
          },
        }
      );

      if (response.data.success) {
        setProjects(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalRecords(response.data.pagination?.totalRecords || 0);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [currentPage, searchTerm, filterStatus]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleDeleteClick = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    try {
      setUpdating(true);
      const token =
        localStorage.getItem("adminToken") || localStorage.getItem("token");

      const response = await axios.delete(
        `http://34.206.193.218:4001/api/admin/adminProjects/delete/${selectedProject._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("Project deleted successfully");
        fetchProjects();
        setShowDeleteModal(false);
        setSelectedProject(null);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error(error.response?.data?.message || "Failed to delete project");
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateProject = async (formData) => {
    try {
      setUpdating(true);
      const token =
        localStorage.getItem("adminToken") || localStorage.getItem("token");

      const response = await axios.put(
        `http://34.206.193.218:4001/api/admin/adminProjects/update/${selectedProject._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success("Project updated successfully");
        fetchProjects();
        setShowEditModal(false);
        setSelectedProject(null);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(error.response?.data?.message || "Failed to update project");
      throw error;
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatNumber = (num) => {
    if (!num) return "N/A";
    return Number(num).toLocaleString();
  };

  const getStatusBadge = (deleted) => {
    return deleted ? (
      <Badge
        bg="danger"
        className="d-flex align-items-center"
        style={{ width: "fit-content" }}
      >
        <XCircle size={14} className="me-1" />
        Deleted
      </Badge>
    ) : (
      <Badge
        bg="success"
        className="d-flex align-items-center"
        style={{ width: "fit-content" }}
      >
        <CheckCircle size={14} className="me-1" />
        Active
      </Badge>
    );
  };

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let number = start; number <= end; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffffff",
        minHeight: "100vh",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ===== HEADER ===== */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3">
            {/* TITLE */}
            <Col md={4}>
              <h4 className="fw-bold mb-0">Projects Management</h4>
              <small className="text-muted">Manage and view all projects</small>
            </Col>

            {/* SEARCH */}
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by project name or user"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </InputGroup>
            </Col>

            {/* STATUS FILTER */}
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <Funnel />
                </InputGroup.Text>
                <Form.Select value={filterStatus} onChange={handleFilterChange}>
                  <option value="active">Active Projects</option>
                  <option value="deleted">Deleted Projects</option>
                  <option value="">All Projects</option>
                </Form.Select>
              </InputGroup>
            </Col>

            {/* REFRESH */}
            <Col md={1} className="text-end">
              <Button
                variant="outline-primary"
                onClick={fetchProjects}
                title="Refresh"
              >
                <ArrowClockwise />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Projects Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5">
              <Search size={48} color="#a0aec0" className="mb-3" />
              <h5 style={{ color: "#718096" }}>No projects found</h5>
              <p className="text-muted">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead
                    style={{
                      backgroundColor: primaryColor,
                      color: "white",
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Project
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Location
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Bid Amount
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Created By
                      </th>
                      <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Status
                      </th>
                      {/* <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Created
                      </th> */}
                      <th
                        style={{
                          padding: "1rem",
                          fontWeight: "600",
                          borderBottom: "none",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project, index) => (
                      <tr
                        key={project._id}
                        style={{
                          backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafc",
                        }}
                      >
                        <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                backgroundColor: secondaryColor,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "600",
                                marginRight: "12px",
                              }}
                            >
                              <Building size={18} />
                            </div>
                            <div>
                              <div
                                style={{ fontWeight: "600", color: "#2d3748" }}
                              >
                                {project.name}
                              </div>
                              {project.lotNo && (
                                <small style={{ color: "#718096" }}>
                                  Lot: {project.lotNo}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{ color: "#4a5568", fontWeight: "500" }}
                            className="d-flex align-items-center"
                          >
                            <GeoAlt size={14} className="me-1" />
                            {project.projectLocation || "N/A"}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{ color: "#4a5568", fontWeight: "500" }}
                            className="d-flex align-items-center"
                          >
                            
                          $ {project.bidAmountTotal || "N/A"}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          {project.createdBy ? (
                            <div className="d-flex align-items-center">
                              {project.createdBy.profilePicture ? (
                                <img
                                  src={project.createdBy.profilePicture}
                                  alt={project.createdBy.name}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    marginRight: "8px",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    backgroundColor: primaryColor,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    marginRight: "8px",
                                  }}
                                >
                                  {getUserInitials(project.createdBy.name)}
                                </div>
                              )}
                              <div>
                                <div
                                  style={{
                                    fontWeight: "500",
                                    fontSize: "14px",
                                  }}
                                >
                                  {project.createdBy.name}
                                </div>
                                <small
                                  style={{ color: "#718096", fontSize: "12px" }}
                                >
                                  {project.createdBy.email}
                                </small>
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted">Unknown</span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          {getStatusBadge(project.deleted)}
                        </td>
                        {/* <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <div
                            style={{ color: "#4a5568" }}
                            className="d-flex align-items-center"
                          >
                            <Calendar size={14} className="me-1" />
                            {formatDate(project.createdAt)}
                          </div>
                        </td> */}
                        <td
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewProject(project)}
                              style={{
                                backgroundColor: secondaryColor,
                                border: "none",
                                color: "white",
                              }}
                              title="View Details"
                            >
                              <Eye size={16} />
                            </Button>
                            {!project.deleted && (
                              <>
                                {/* <Button
                                  variant="warning"
                                  size="sm"
                                  onClick={() => handleEditProject(project)}
                                  style={{
                                    backgroundColor: "#ffc107",
                                    border: "none",
                                    color: "white",
                                  }}
                                  title="Edit Project"
                                >
                                  <Pencil size={16} />
                                </Button> */}
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDeleteClick(project)}
                                  style={{
                                    backgroundColor: "#dc3545",
                                    border: "none",
                                    color: "white",
                                  }}
                                  title="Delete Project"
                                >
                                  <Trash size={16} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Showing {(currentPage - 1) * limit + 1} to{" "}
                    {Math.min(currentPage * limit, totalRecords)} of{" "}
                    {totalRecords} projects
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* View Project Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: primaryColor, color: "white" }}
        >
          <Modal.Title className="d-flex align-items-center">
            <Building className="me-2" />
            Project Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          {selectedProject && (
            <Row className="g-4">
              <Col md={12}>
                <div className="d-flex align-items-center mb-4">
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      backgroundColor: secondaryColor,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "600",
                      marginRight: "16px",
                    }}
                  >
                    <Building size={24} />
                  </div>
                  <div>
                    <h4
                      style={{ color: "#2d3748", fontWeight: "600", margin: 0 }}
                    >
                      {selectedProject.name}
                    </h4>
                    <div className="d-flex gap-2 mt-1">
                      {getStatusBadge(selectedProject.deleted)}
                      {selectedProject.lotNo && (
                        <Badge bg="secondary">
                          Lot: {selectedProject.lotNo}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <Card style={{ border: `2px solid ${secondaryColor}` }}>
                  <Card.Header
                    style={{
                      backgroundColor: secondaryColor,
                      color: "white",
                      fontWeight: "600",
                    }}
                  >
                    Project Information
                  </Card.Header>
                  <Card.Body>
                    <div className="row g-3">
                      <div className="col-12">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Location
                        </label>
                        <div
                          style={{ color: "#2d3748", fontWeight: "500" }}
                          className="d-flex align-items-center"
                        >
                          <GeoAlt className="me-2" size={16} />
                          {selectedProject.projectLocation || "Not specified"}
                        </div>
                      </div>
                      <div className="col-12">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Description
                        </label>
                        <div style={{ color: "#2d3748", fontWeight: "500" }}>
                          {selectedProject.description ||
                            "No description provided"}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card style={{ border: `2px solid ${primaryColor}` }}>
                  <Card.Header
                    style={{
                      backgroundColor: primaryColor,
                      color: "white",
                      fontWeight: "600",
                    }}
                  >
                    Measurements (sq ft)
                  </Card.Header>
                  <Card.Body>
                    <div className="row g-3">
                      <div className="col-6">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Living Area
                        </label>
                        <div
                          style={{
                            color: "#2d3748",
                            fontWeight: "500",
                            fontSize: "1.1rem",
                          }}
                        >
                          {formatNumber(selectedProject.livingSqft)}
                        </div>
                      </div>
                      <div className="col-6">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Slab Area
                        </label>
                        <div
                          style={{
                            color: "#2d3748",
                            fontWeight: "500",
                            fontSize: "1.1rem",
                          }}
                        >
                          {formatNumber(selectedProject.slabSqft)}
                        </div>
                      </div>
                      <div className="col-6">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Roof Area
                        </label>
                        <div
                          style={{
                            color: "#2d3748",
                            fontWeight: "500",
                            fontSize: "1.1rem",
                          }}
                        >
                          {formatNumber(selectedProject.areaRoofSqft)}
                        </div>
                      </div>
                      <div className="col-6">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Flatwork Area
                        </label>
                        <div
                          style={{
                            color: "#2d3748",
                            fontWeight: "500",
                            fontSize: "1.1rem",
                          }}
                        >
                          {formatNumber(selectedProject.flatworkSqft)}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card style={{ border: "2px solid #e2e8f0" }}>
                  <Card.Header
                    style={{
                      backgroundColor: "#e2e8f0",
                      color: "#2d3748",
                      fontWeight: "600",
                    }}
                  >
                    Created By
                  </Card.Header>
                  <Card.Body>
                    {selectedProject.createdBy ? (
                      <div className="d-flex align-items-center">
                        {selectedProject.createdBy.profilePicture ? (
                          <img
                            src={selectedProject.createdBy.profilePicture}
                            alt={selectedProject.createdBy.name}
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              marginRight: "12px",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              backgroundColor: primaryColor,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontWeight: "600",
                              marginRight: "12px",
                            }}
                          >
                            {getUserInitials(selectedProject.createdBy.name)}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: "600", color: "#2d3748" }}>
                            {selectedProject.createdBy.name}
                          </div>
                          <small style={{ color: "#718096" }}>
                            {selectedProject.createdBy.email}
                          </small>
                          <br />
                          <small style={{ color: "#718096" }}>
                            Phone: {selectedProject.createdBy.number || "N/A"}
                          </small>
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted">Unknown</span>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card style={{ border: "2px solid #e2e8f0" }}>
                  <Card.Header
                    style={{
                      backgroundColor: "#e2e8f0",
                      color: "#2d3748",
                      fontWeight: "600",
                    }}
                  >
                    Timestamps
                  </Card.Header>
                  <Card.Body>
                    <div className="row g-2">
                      <div className="col-12">
                        <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                          Created
                        </label>
                        <div
                          style={{ color: "#2d3748", fontWeight: "500" }}
                          className="d-flex align-items-center"
                        >
                          <Calendar className="me-2" size={14} />
                          {formatDate(selectedProject.createdAt)}
                        </div>
                      </div>
                      {selectedProject.updatedAt && (
                        <div className="col-12">
                          <label
                            style={{ color: "#718096", fontSize: "0.9rem" }}
                          >
                            Last Updated
                          </label>
                          <div
                            style={{ color: "#2d3748", fontWeight: "500" }}
                            className="d-flex align-items-center"
                          >
                            <Calendar className="me-2" size={14} />
                            {formatDate(selectedProject.updatedAt)}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={12}>
                <Card style={{ border: "2px solid #e2e8f0" }}>
                  <Card.Header
                    style={{
                      backgroundColor: "#e2e8f0",
                      color: "#2d3748",
                      fontWeight: "600",
                    }}
                  >
                    Bid Information
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                        Bid Amount
                      </label>
                      <div style={{ fontSize: "1.25rem", fontWeight: "600" }}>
                        {selectedProject.bidAmountTotal
                          ? `$ ${Number(
                              selectedProject.bidAmountTotal
                            ).toLocaleString()}`
                          : "N/A"}
                      </div>
                    </div>

                    <div>
                      <label style={{ color: "#718096", fontSize: "0.9rem" }}>
                        Bid Estimate Notes
                      </label>
                      <div style={{ color: "#2d3748" }}>
                        {selectedProject.bidNote || "No notes added"}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* Edit Project Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: primaryColor, color: "white" }}
        >
          <Modal.Title className="d-flex align-items-center">
            <Pencil className="me-2" />
            Edit Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <EditProjectForm
              project={selectedProject}
              onSubmit={handleUpdateProject}
              loading={updating}
              onCancel={() => setShowEditModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#dc3545", color: "white" }}
        >
          <Modal.Title className="d-flex align-items-center">
            <Trash className="me-2" />
            Delete Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <div className="text-center">
              <div className="mb-4">
                <div style={{ fontSize: "4rem", color: "#dc3545" }}>
                  <Trash />
                </div>
              </div>

              <h5 style={{ color: "#2d3748", marginBottom: "1rem" }}>
                Delete {selectedProject.name}?
              </h5>

              <p className="text-muted">
                This will mark the project as deleted. The action can be
                reversed by changing the status back to active.
              </p>

              <div className="alert alert-warning">
                <strong>Note:</strong> This is a soft delete. Project data will
                be preserved but marked as deleted.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteProject}
            disabled={updating}
          >
            {updating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash className="me-2" />
                Delete Project
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Edit Project Form Component
const EditProjectForm = ({ project, onSubmit, loading, onCancel }) => {
  const [formData, setFormData] = useState({
    name: project.name || "",
    projectLocation: project.projectLocation || "",
    livingSqft: project.livingSqft || "",
    slabSqft: project.slabSqft || "",
    areaRoofSqft: project.areaRoofSqft || "",
    flatworkSqft: project.flatworkSqft || "",
    lotNo: project.lotNo || "",
    description: project.description || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
    } catch (error) {
      // Error is handled in parent component
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className="row g-3">
        <div className="col-12">
          <Form.Group>
            <Form.Label>Project Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </div>

        <div className="col-12">
          <Form.Group>
            <Form.Label>Location</Form.Label>
            <Form.Control
              type="text"
              name="projectLocation"
              value={formData.projectLocation}
              onChange={handleChange}
              placeholder="Enter project location"
            />
          </Form.Group>
        </div>

        <div className="col-6">
          <Form.Group>
            <Form.Label>Living Area (sq ft)</Form.Label>
            <Form.Control
              type="number"
              name="livingSqft"
              value={formData.livingSqft}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </Form.Group>
        </div>

        <div className="col-6">
          <Form.Group>
            <Form.Label>Slab Area (sq ft)</Form.Label>
            <Form.Control
              type="number"
              name="slabSqft"
              value={formData.slabSqft}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </Form.Group>
        </div>

        <div className="col-6">
          <Form.Group>
            <Form.Label>Roof Area (sq ft)</Form.Label>
            <Form.Control
              type="number"
              name="areaRoofSqft"
              value={formData.areaRoofSqft}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </Form.Group>
        </div>

        <div className="col-6">
          <Form.Group>
            <Form.Label>Flatwork Area (sq ft)</Form.Label>
            <Form.Control
              type="number"
              name="flatworkSqft"
              value={formData.flatworkSqft}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </Form.Group>
        </div>

        <div className="col-12">
          <Form.Group>
            <Form.Label>Lot Number</Form.Label>
            <Form.Control
              type="text"
              name="lotNo"
              value={formData.lotNo}
              onChange={handleChange}
              placeholder="Enter lot number"
            />
          </Form.Group>
        </div>

        <div className="col-12">
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter project description"
            />
          </Form.Group>
        </div>
      </div>

      <div className="d-flex gap-2 mt-4">
        <Button
          variant="secondary"
          onClick={onCancel}
          className="flex-grow-1"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          className="flex-grow-1"
          disabled={loading}
          style={{ backgroundColor: "#886b1bff", border: "none" }}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            "Update Project"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default Projects;
